import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { ROLES, isActive } from "../lib/auth";
import { DAYS_OF_WEEK } from "../lib/types";
import StudentForm from "../components/StudentForm";

export default function AddStudent() {
    const { addStudent } = useData();
    const { settings } = useSettings();
    const { isAdmin, workspaceMode, teachers, setWorkspaceMode } = useAuth();
    const navigate = useNavigate();

    const availableDays = DAYS_OF_WEEK.filter((d) =>
        settings.workingDays.includes(d),
    );

    const noWorkspace = isAdmin && workspaceMode === "all";
    const teacherWorkspaces = teachers.filter(
        (t) => t.role === ROLES.TEACHER && isActive(t),
    );

    const handleSubmit = (payload) => {
        const student = addStudent(payload);
        if (!student) return; // requireWorkspace already toasted
        toast.success(`${student.name} added`);
        navigate(`/students/${student.id}`);
    };

    const focusFirstTeacher = () => {
        const first = teacherWorkspaces[0];
        if (!first) {
            navigate("/admin/teachers");
            return;
        }
        setWorkspaceMode(first.id);
        toast.success(`Switched to ${first.name}'s workspace`);
    };

    return (
        <div className="max-w-3xl mx-auto space-y-6" data-testid="add-student-page">
            <Link
                to="/students"
                className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-800"
                data-testid="back-to-students-link"
            >
                <ArrowLeft size={16} strokeWidth={2.8} />
                Back to students
            </Link>
            <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">
                    New
                </div>
                <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tight">
                    Add Student
                </h1>
            </div>

            {noWorkspace && (
                <div
                    className="bg-warning-soft border border-warning/30 rounded-3xl px-5 sm:px-6 py-4 flex items-start sm:items-center gap-3 flex-col sm:flex-row"
                    data-testid="addstudent-no-workspace-banner"
                >
                    <div className="w-9 h-9 rounded-xl bg-warning-soft text-warning flex items-center justify-center shrink-0">
                        <AlertTriangle size={18} strokeWidth={2.6} />
                    </div>
                    <div className="flex-1">
                        <div className="font-bold text-sm text-warning">
                            {teacherWorkspaces.length === 0
                                ? "No teachers yet"
                                : "Pick a teacher workspace"}
                        </div>
                        <div className="text-xs text-warning/80">
                            {teacherWorkspaces.length === 0
                                ? "Create a teacher in Manage Teachers before adding students."
                                : "Students belong to a teacher's workspace. Switch into one to add."}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={focusFirstTeacher}
                        className="focus-ring rounded-xl bg-warning hover:bg-warning/85 text-white font-bold h-10 px-4 text-sm"
                        data-testid="addstudent-fix-workspace-btn"
                    >
                        {teacherWorkspaces.length === 0
                            ? "Open Manage Teachers"
                            : `Switch to ${teacherWorkspaces[0].name}`}
                    </button>
                </div>
            )}

            <div data-testid="add-student-form">
                <StudentForm
                    availableDays={availableDays}
                    submitDisabled={noWorkspace}
                    submitLabel="Save Student"
                    onSubmit={handleSubmit}
                />
            </div>
        </div>
    );
}
